/**
 * WHERE A RECORD OPENS, BY WHO IS LOOKING (fe#815).
 *
 * Search results and the AI assistant's record links used office routes only:
 * /jobs/:id, /equipment/:id, /tickets/:id, /trucking/bols/:id. A field hand's
 * route tree has none of them, so every one of those links fell through to the
 * tree's catch-all and dropped the hand back on their home screen.
 *
 * Three audiences, decided the way App.tsx picks a route tree:
 *   office        — not a field worker: the office routes, unchanged
 *   casing-field  — a field worker in the casing tree (/field) whose tenant has
 *                   casing_running: the field job and equipment pages
 *   my-work       — a field worker on a trucking or rentals tenant (/my-work):
 *                   a driver's BOL page
 *   none          — any other field worker (a downhole or PT hand in the casing
 *                   tree): no record page search could open
 *
 * `null` means "there is no page for this here". The caller renders the record
 * as plain text, or leaves it out of search, rather than linking it home.
 */
export type RecordKind = 'job' | 'equipment' | 'bol' | 'ticket';
export type RecordAudience = 'office' | 'casing-field' | 'my-work' | 'none';

export function recordAudience(isFieldWorker: boolean, hasVertical: (key: string) => boolean): RecordAudience {
  if (!isFieldWorker) return 'office';
  if (hasVertical('trucking') || hasVertical('surface_rentals')) return 'my-work';
  return hasVertical('casing_running') ? 'casing-field' : 'none';
}

export function recordPath(kind: RecordKind, id: string, audience: RecordAudience): string | null {
  const e = encodeURIComponent(id);
  switch (audience) {
    case 'office':
      return { job: `/jobs/${e}`, equipment: `/equipment/${e}`, bol: `/trucking/bols/${e}`, ticket: `/tickets/${e}` }[kind];
    case 'casing-field':
      // No field page opens a single ticket by id; the hand's tickets are a list.
      return kind === 'job' ? `/field/jobs/${e}` : kind === 'equipment' ? `/field/equipment/${e}` : null;
    case 'my-work':
      // GET /search returns a load's id for a BOL, and /driver/bols/:bolId reads
      // /trucking/loads/bols/:id — the same id.
      return kind === 'bol' ? `/driver/bols/${e}` : null;
    case 'none':
      return null;
  }
}

/**
 * The search groups a field audience can open. The API already returns only
 * jobs, equipment and BOLs to a field_worker (api#1138); this narrows to the
 * ones this tree has a page for. An empty list means Search is not offered at
 * all: a rental tech, or a downhole or PT hand, has nothing it could find.
 */
export function fieldSearchGroups(audience: RecordAudience, hasVertical: (key: string) => boolean): ('jobs' | 'equipment' | 'bills_of_lading')[] {
  if (audience === 'casing-field') return ['jobs', 'equipment'];
  if (audience === 'my-work') return hasVertical('trucking') ? ['bills_of_lading'] : [];
  return [];
}
